import * as T from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// 3D models from the model library (the Kenney kits, CC0, and later the admin's uploads): each file is loaded
// once and shared by every copy; a copy is fitted into the space the content gives the thing (keeping the
// model's own proportions), standing on the floor at the origin and facing +z. Until a model arrives (or if it
// cannot), the block drawing stays, so nothing is ever missing.

let base = '/models/';
// where the library is served from (the web app serves it itself; the admin points at the same files)
export function setModelBase(url: string) {
  base = url.endsWith('/') ? url : `${url}/`;
}

// a file in the library, by its path inside it
export const modelUrl = (file: string) => `${base}${file}`;

const sources = new Map<string, Promise<T.Object3D>>();
export function loadModel(id: string) {
  let source = sources.get(id);
  if (!source) {
    source = new GLTFLoader()
      .loadAsync(`${base}${id}.glb`)
      .then((gltf) => {
        gltf.scene.traverse((object) => {
          if (object instanceof T.Mesh) {
            object.castShadow = true;
            object.receiveShadow = true;
            // the kits colour with a small sheet of flat swatches: read it pixel for pixel, never blurred, or a
            // far-off house averages every swatch into one colour
            for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
              const map = (material as T.MeshStandardMaterial).map;
              if (!map) continue;
              map.magFilter = T.NearestFilter;
              map.minFilter = T.NearestFilter;
              map.generateMipmaps = false;
              map.needsUpdate = true;
            }
          }
        });
        return gltf.scene;
      })
      .catch((error) => {
        sources.delete(id); // a failed download can be tried again
        throw error;
      });
    sources.set(id, source);
  }
  return source;
}

export type ModelFit = { w: number; d: number; h: number };
export type FitOptions = {
  // materials (by name) painted in a colour, e.g. { carpet: '#4f7a63' }
  paint?: Record<string, string>;
  // a row of copies along the width instead of one copy (a counter)
  row?: boolean;
  // every material made for the paint is handed here, to be freed with the scene
  own?: (material: T.Material) => void;
};

// paint the named materials of a copy (each copy gets its own painted material; the rest stay shared)
function paint(copy: T.Object3D, colours: Record<string, string>, own?: (material: T.Material) => void) {
  const made = new Map<T.Material, T.Material>();
  copy.traverse((object) => {
    if (!(object instanceof T.Mesh)) return;
    const swap = (material: T.Material) => {
      const colour = colours[material.name];
      if (!colour) return material;
      let painted = made.get(material);
      if (!painted) {
        painted = material.clone();
        (painted as T.MeshStandardMaterial).color?.set(colour);
        made.set(material, painted);
        own?.(painted);
      }
      return painted;
    };
    object.material = Array.isArray(object.material) ? object.material.map(swap) : swap(object.material);
  });
}

// a copy of a loaded model fitted into a w × d footprint (no taller than h), centred, on the floor
export function fitModel(source: T.Object3D, size: ModelFit, options: FitOptions = {}) {
  const own = new T.Box3().setFromObject(source).getSize(new T.Vector3());
  const holder = new T.Group();
  const scaleFor = (width: number) =>
    Math.min(width / Math.max(own.x, 0.01), size.d / Math.max(own.z, 0.01), size.h / Math.max(own.y, 0.01));
  const scale = options.row
    ? Math.min(size.d / Math.max(own.z, 0.01), size.h / Math.max(own.y, 0.01))
    : scaleFor(size.w);
  const count = options.row ? Math.max(1, Math.round(size.w / (own.x * scale))) : 1;
  for (let index = 0; index < count; index += 1) {
    const copy = source.clone(true);
    if (options.paint) paint(copy, options.paint, options.own);
    copy.scale.setScalar(scale);
    copy.updateMatrixWorld(true);
    const box = new T.Box3().setFromObject(copy);
    const centre = box.getCenter(new T.Vector3());
    const step = own.x * scale;
    copy.position.set(-centre.x + (index - (count - 1) / 2) * step, -box.min.y, -centre.z);
    holder.add(copy);
  }
  // a row is stretched a little to fill the counter exactly
  if (options.row) holder.scale.x = size.w / (count * own.x * scale);
  return holder;
}

// put a thing's model in once it has loaded and hide its block drawing; returns a cancel for a scene that goes
// away first. `blocks` is the group holding the block drawing.
export function swapInModel(piece: T.Group, blocks: T.Object3D, id: string, size: ModelFit, options: FitOptions = {}) {
  let cancelled = false;
  loadModel(id)
    .then((source) => {
      if (cancelled) return;
      piece.add(fitModel(source, size, options));
      blocks.visible = false;
    })
    .catch(() => {
      // the blocks stay: a missing model never leaves a hole
    });
  return () => {
    cancelled = true;
  };
}
